import * as React from 'react';

import { TextInput } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    LoadingIndicator,
    PageSplitDescription,
    PageSplitTitle,
} from '@gitbook/spine-react';
import { localStorage } from '@gitbook/util-local-storage';
import { useHeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFAScreenModal } from '../../Components';
import { useHistory, useSetAnalyticsScreen } from '../../navigation';
import { useAuthOperations } from '../../state';
import { STORAGE_EMAIL_KEY } from './constants';
import { LoginScreenLayout } from './LoginScreenLayout';

/**
 * Step to use the sign-in link.
 */
export function StepApplySignInLink(): React.ReactElement {
    const history = useHistory();
    const { mfa, signInWithEmailLink } = useAuthOperations();
    const [loading, wrapLoading, setLoading] = useLoadingState(true);
    const [promptedEmail, setPromptedEmail] = React.useState('');

    useHeadTitle('Sign in');
    useSetAnalyticsScreen('login_apply_signin_link');

    const loginWithEmail = async (email: string) => {
        wrapLoading(async () => {
            await signInWithEmailLink(email, window.location.href);

            // Remove the link's query string from the URL
            history.replace({ pathname: history.location.pathname });
        });
    };

    const onSubmitEmail = () => {
        loginWithEmail(promptedEmail);
    };

    // Auto-login using stored email or prompt email
    React.useEffect(() => {
        (async () => {
            const email = await localStorage.getAsync<string>(STORAGE_EMAIL_KEY);
            if (email) {
                await loginWithEmail(email);
            } else {
                setLoading(false);
            }
        })();
    }, []);

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle logo={<Icon.LogoSquare size={32} />}>
                {loading ? 'Signing you in...' : 'Confirm your email address'}
            </PageSplitTitle>
            <PageSplitDescription>
                {loading ? '' : 'We need to confirm your email address before sign-in.'}
            </PageSplitDescription>
            {loading ? null : (
                <TextInput
                    placeholder="name@example.com"
                    value={promptedEmail}
                    onChangeText={setPromptedEmail}
                    onSubmit={onSubmitEmail}
                />
            )}
            <div className="flex w-full flex-col items-center gap-4 px-10">
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <Button
                        disabled={loading}
                        kind="primary"
                        onPress={onSubmitEmail}
                        label="Continue"
                        size="large"
                        className="w-full"
                    />
                )}
            </div>
        </LoginScreenLayout>
    );
}
